type CachedStudent = {
  id: string;
  firstName?: string;
  lastName?: string;
  initials?: string;
  hue?: string;
  sex?: "homme" | "femme";
  photoUrl?: string;
  participation?: "A" | "B" | "C" | "D";
  absent?: boolean;
  results?: Record<string, "A" | "B" | "C" | "D">;
  resultUpdatedAt?: Record<string, string>;
  officialGroup?: "A" | "B";
};

type ClassSeed = {
  id: string;
  students?: CachedStudent[];
};

type CachedPlan = {
  groups?: Array<{ id: string; name?: string }>;
  locations?: Record<string, string[]>;
};

const colors = ["#1565c0", "#8e3a75", "#3d6f45", "#b45a2a", "#365c9a", "#7c4a9e", "#9b3d43", "#32746d"];
const planPrefix = "competenceo-plan-v1:";

function validParticipation(value: unknown): value is "A" | "B" | "C" | "D" {
  return value === "A" || value === "B" || value === "C" || value === "D";
}

function sanitizeStudentList(value: unknown) {
  if (!Array.isArray(value)) return { value, changed: false };
  let changed = false;
  const students = value.map((entry) => {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) return entry;
    const student = entry as Record<string, unknown>;
    const participation = validParticipation(student.participation) ? student.participation : "B";
    const absent = typeof student.absent === "boolean" ? student.absent : false;
    if (student.participation === participation && student.absent === absent) return entry;
    changed = true;
    return { ...student, participation, absent };
  });
  return { value: students, changed };
}

function sanitizeStoredValue(key: string, raw: string | null) {
  if (!raw) return raw;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (key.startsWith("competenceo-students-v1:") || key === "competenceo-demo-students") {
      const sanitized = sanitizeStudentList(parsed);
      return sanitized.changed ? JSON.stringify(sanitized.value) : raw;
    }
    if (key.startsWith("competenceo-classroom-") && parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      const classroom = parsed as Record<string, unknown>;
      const sanitized = sanitizeStudentList(classroom.students);
      return sanitized.changed ? JSON.stringify({ ...classroom, students: sanitized.value }) : raw;
    }
    return raw;
  } catch {
    return raw;
  }
}

function installStudentStorageGuard() {
  const scope = globalThis as typeof globalThis & { __competenceoStudentStorageGuard?: boolean };
  if (scope.__competenceoStudentStorageGuard || typeof Storage === "undefined") return;
  const originalGetItem = Storage.prototype.getItem;
  const originalSetItem = Storage.prototype.setItem;

  Storage.prototype.getItem = function getItem(key: string) {
    return sanitizeStoredValue(key, originalGetItem.call(this, key));
  };
  Storage.prototype.setItem = function setItem(key: string, value: string) {
    const safeValue = sanitizeStoredValue(key, value) ?? value;
    originalSetItem.call(this, key, safeValue);
  };

  scope.__competenceoStudentStorageGuard = true;
}

function newSpaceId() {
  return `space-repair-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function safeJson<T>(raw: string | null, fallback: T): T {
  if (!raw) return fallback;
  try { return JSON.parse(raw) as T; }
  catch { return fallback; }
}

function studentsForPlan(schoolClass: ClassSeed, cohort: string) {
  const students = Array.isArray(schoolClass.students) ? schoolClass.students : [];
  if (cohort !== "A" && cohort !== "B") return students;
  return students.filter((student) => student.officialGroup === cohort);
}

function repairPlan(schoolClass: ClassSeed, key: string, plan: CachedPlan) {
  const cohort = key.slice(key.lastIndexOf(":") + 1);
  const validStudentIds = new Set(studentsForPlan(schoolClass, cohort).map((student) => student.id));
  const groups = Array.isArray(plan.groups)
    ? plan.groups.filter((group) => group && typeof group.id === "string" && group.id.trim())
    : [];
  const validGroupIds = new Set(groups.map((group) => group.id));
  const sourceLocations = plan.locations && typeof plan.locations === "object" ? plan.locations : {};
  const locations: Record<string, string[]> = {};
  const usedStudents = new Set<string>();

  for (const group of groups) {
    const members = Array.isArray(sourceLocations[group.id]) ? sourceLocations[group.id] : [];
    locations[group.id] = members.filter((id) => {
      if (!validStudentIds.has(id) || usedStudents.has(id)) return false;
      usedStudents.add(id);
      return true;
    });
  }

  const sourceRoom = Array.isArray(sourceLocations.room) ? sourceLocations.room : [];
  const room = sourceRoom.map((id) => {
    if (typeof id !== "string") return newSpaceId();
    if (id.startsWith("space-")) return id;
    if (id.startsWith("group:")) return validGroupIds.has(id.slice("group:".length)) ? id : newSpaceId();
    if (!validStudentIds.has(id) || usedStudents.has(id)) return newSpaceId();
    usedStudents.add(id);
    return id;
  });

  for (const studentId of validStudentIds) {
    if (usedStudents.has(studentId)) continue;
    const emptyIndex = room.findIndex((id) => id.startsWith("space-"));
    if (emptyIndex >= 0) room[emptyIndex] = studentId;
    else room.push(studentId);
    usedStudents.add(studentId);
  }

  locations.room = room;
  return { groups, locations };
}

function reconcileStudentCacheAndPlans() {
  const classes = safeJson<ClassSeed[]>(localStorage.getItem("competenceo-classes-v1"), []);
  if (!Array.isArray(classes)) return;

  const storageKeys = Array.from({ length: localStorage.length }, (_, index) => localStorage.key(index)).filter((key): key is string => Boolean(key));

  for (const schoolClass of classes) {
    if (!schoolClass?.id || !Array.isArray(schoolClass.students)) continue;

    const studentKey = `competenceo-students-v1:${schoolClass.id}`;
    const cached = safeJson<CachedStudent[]>(localStorage.getItem(studentKey), []);
    const byId = new Map((Array.isArray(cached) ? cached : []).filter((student) => student?.id).map((student) => [student.id, student]));
    const merged = schoolClass.students.map((seed, index) => {
      const previous: CachedStudent = byId.get(seed.id) ?? { id: seed.id };
      return {
        ...previous,
        ...seed,
        initials: previous.initials ?? `${String(seed.firstName ?? "").charAt(0)}${String(seed.lastName ?? "").charAt(0)}`.toUpperCase(),
        hue: previous.hue ?? colors[index % colors.length],
        participation: validParticipation(previous.participation) ? previous.participation : "B",
        absent: typeof previous.absent === "boolean" ? previous.absent : false,
        results: previous.results ?? {},
        resultUpdatedAt: previous.resultUpdatedAt ?? {},
      } satisfies CachedStudent;
    });

    try { localStorage.setItem(studentKey, JSON.stringify(merged)); }
    catch { /* L'application continue avec les données déjà disponibles. */ }

    const classPlanPrefix = `${planPrefix}${schoolClass.id}:`;
    for (const key of storageKeys.filter((item) => item.startsWith(classPlanPrefix))) {
      const plan = safeJson<CachedPlan | null>(localStorage.getItem(key), null);
      if (!plan) continue;
      try { localStorage.setItem(key, JSON.stringify(repairPlan(schoolClass, key, plan))); }
      catch { /* Un plan illisible ne doit jamais empêcher l'ouverture de la classe. */ }
    }
  }
}

// Installé avant le premier rendu React : les anciennes valeurs peuvent être
// réinjectées par la synchronisation après la réparation initiale. On filtre donc
// aussi chaque lecture/écriture des caches élèves.
installStudentStorageGuard();
reconcileStudentCacheAndPlans();
